import { inject, Injectable } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, combineLatest } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { FeatureFlags } from '@app/core/feature-flags/feature-flags';
import { LaunchDarklyService } from '@app/core/feature-flags/launchdarkly.service';
import { LinksService } from '@app/core/links.service';
import { MembershipGraphQL } from '@app/core/membership-graphql.service';
import { MembershipService } from '@app/core/membership.service';
import { NativeAppService, NativeAppActions } from '@app/shared/native-app.service';

import { MembershipAnalyticsService } from './membership-analytics.service';
import { MembershipRenewalInterstitialService } from './membership-renewal-interstitial.service';

@Injectable({
  providedIn: 'root',
})
class MembershipRenewalHomePageRedirectGuardService {
  constructor(
    private launchDarklyService: LaunchDarklyService,
    private membershipGraphQL: MembershipGraphQL,
    private membershipService: MembershipService,
    private router: Router,
    private interstitialService: MembershipRenewalInterstitialService,
    private linksService: LinksService,
    private nativeAppService: NativeAppService,
    private membershipAnalyticsService: MembershipAnalyticsService,
  ) {}

  canActivate(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    const membership$ = this.membershipService.getMembership();

    return combineLatest([membership$, this.membershipGraphQL.fetch()]).pipe(
      tap(([membership]) => {
        const isWebView = this.nativeAppService.isWebView(undefined);
        const hasActiveMembership = membership?.isActive;

        if (hasActiveMembership && isWebView) {
          this.membershipAnalyticsService.trackNativeAppGoHomeTriggered('membership_renewal_home_page_redirect_guard');
          this.nativeAppService.invokeAndroidAndiOS(NativeAppActions.GO_HOME);
        }
      }),
      map(([_membership, result]) => result?.data?.membership?.isLimitedAccessEligible || false),
      switchMap(isLimitedAccessEligible =>
        this.launchDarklyService.featureFlag$(FeatureFlags.EXPIRED_TO_GUEST_CONVERSION_WEB, false).pipe(
          map(expiredToGuestConversionWeb => {
            if (!expiredToGuestConversionWeb) {
              return true;
            }

            if (isLimitedAccessEligible) {
              return this.router.parseUrl('/membership/welcome-back');
            }

            return true;
          }),
        ),
      ),
    );
  }
}

export const MembershipRenewalHomePageRedirectGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): Observable<boolean | UrlTree> => inject(MembershipRenewalHomePageRedirectGuardService).canActivate(route, state);
